import { useState, useEffect, useCallback, useMemo } from 'react';
import { puzzles, Puzzle } from '../lib/puzzles';

export type CellState = 'empty' | 'filled' | 'marked';
export type Language = 'pl' | 'ua';
export type Difficulty = 'easy' | 'medium' | 'hard';

export function useGameLogic() {
  const [darkMode, setDarkMode] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [language, setLanguage] = useState<Language>('pl');
  const [difficulty, setDifficulty] = useState<Difficulty>('easy');
  const [puzzle, setPuzzle] = useState<Puzzle | null>(null);
  const [grid, setGrid] = useState<CellState[][]>([]);
  const [hints, setHints] = useState(3);
  const [score, setScore] = useState(0);
  const [time, setTime] = useState(0);
  const [gameWon, setGameWon] = useState(false);
  const [timerRunning, setTimerRunning] = useState(false);
  const [clickMode, setClickMode] = useState<'fill' | 'x'>('fill');
  const [showStats, setShowStats] = useState(false);
  const [showAchievements, setShowAchievements] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showResult, setShowResult] = useState<'correct' | 'incorrect' | null>(null);

  // Init dark mode
  useEffect(() => {
    const isDark = localStorage.getItem('darkMode') === 'true' ||
      window.matchMedia('(prefers-color-scheme: dark)').matches;
    setDarkMode(isDark);
    if (isDark) document.documentElement.classList.add('dark');
  }, []);

  const toggleDarkMode = useCallback(() => {
    setDarkMode(prev => {
      const next = !prev;
      localStorage.setItem('darkMode', String(next));
      if (next) document.documentElement.classList.add('dark');
      else document.documentElement.classList.remove('dark');
      return next;
    });
  }, []);

  // Init puzzle when difficulty changes
  useEffect(() => {
    const puzList = puzzles.filter((p: Puzzle) => p.difficulty === difficulty);
    const randomPuzzle = puzList[Math.floor(Math.random() * puzList.length)];
    setPuzzle(randomPuzzle);
    setGrid(Array(randomPuzzle.size).fill(null).map(() => Array(randomPuzzle.size).fill('empty')));
    setHints(3);
    setScore(0);
    setTime(0);
    setGameWon(false);
    setTimerRunning(false);
    setShowResult(null);
  }, [difficulty]);

  // Timer
  useEffect(() => {
    if (timerRunning && !gameWon && puzzle) {
      const interval = setInterval(() => setTime(t => t + 1), 1000);
      return () => clearInterval(interval);
    }
  }, [timerRunning, gameWon, puzzle]);

  const toggleCell = useCallback((row: number, col: number) => {
    if (gameWon || !puzzle) return;
    const newGrid = grid.map(r => [...r]);
    const current = newGrid[row][col];
    if (clickMode === 'fill') {
      newGrid[row][col] = current === 'filled' ? 'empty' : 'filled';
    } else if (clickMode === 'x') {
      newGrid[row][col] = current === 'marked' ? 'empty' : 'marked';
    }
    setGrid(newGrid);
  }, [gameWon, puzzle, grid, clickMode]);

  const validate = useCallback((): boolean => {
    if (!puzzle) return false;
    const size = puzzle.size;
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        if (puzzle.solution[y][x] !== (grid[y][x] === 'filled')) {
          return false;
        }
      }
    }
    return true;
  }, [puzzle, grid]);

  const handleValidate = useCallback(() => {
    if (validate()) {
      setGameWon(true);
      setTimerRunning(false);
      setShowResult('correct');
    } else {
      setShowResult('incorrect');
    }
  }, [validate]);

  const handleHint = useCallback(() => {
    if (hints <= 0 || !puzzle) return;
    const unfilled: {y: number; x: number}[] = [];
    for (let y = 0; y < puzzle.size; y++) {
      for (let x = 0; x < puzzle.size; x++) {
        if (puzzle.solution[y][x] && grid[y][x] !== 'filled') {
          unfilled.push({ y, x });
        }
      }
    }
    if (unfilled.length > 0) {
      const random = unfilled[Math.floor(Math.random() * unfilled.length)];
      const newGrid = grid.map(r => [...r]);
      newGrid[random.y][random.x] = 'filled';
      setGrid(newGrid);
      setHints(h => h - 1);
    }
  }, [hints, puzzle, grid]);

  const resetGrid = useCallback(() => {
    if (puzzle) {
      setGrid(Array(puzzle.size).fill(null).map(() => Array(puzzle.size).fill('empty')));
    }
  }, [puzzle]);

  const handleNewGame = useCallback(() => {
    window.location.reload();
  }, []);

  const formatTime = useCallback((seconds: number): string => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  }, []);

  const cellSize = useMemo(() => {
    if (!puzzle) return 30;
    return Math.max(24, Math.min(40, Math.floor(320 / puzzle.size)));
  }, [puzzle]);

  return {
    // State
    darkMode,
    soundEnabled,
    language,
    difficulty,
    puzzle,
    grid,
    hints,
    score,
    time,
    gameWon,
    timerRunning,
    clickMode,
    showStats,
    showAchievements,
    mobileMenuOpen,
    showResult,
    // Actions
    setSoundEnabled,
    setLanguage,
    setDifficulty,
    setTimerRunning,
    setClickMode,
    setShowStats,
    setShowAchievements,
    setMobileMenuOpen,
    toggleDarkMode,
    toggleCell,
    handleValidate,
    handleHint,
    handleNewGame,
    resetGrid,
    formatTime,
    validate,
    cellSize,
  };
}
